import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import qs from 'qs';

import { PageContext } from '../components/page';
import PageContentWrapper from '../components/pageContentWrapper';
import { BodyCard } from '../components/styledComponent';
import { FlightSearchFilter } from './FlightSearchFilters';
import { flightSearch } from '../../service/flightService';
import FlightSearchResultsTable from './FlightSearchResultsTable';
import { ProcessedFlightHistory } from '../../types/flight';
import { getQueryField } from '../../lib/url';
import FullPageLoadingSpinner from '../../pages/components/fullPageLoadingSpinner'; // Import the spinner
import { FLIGHT_SEARCH_DATE_FORMAT } from '../../constants';

const FlightSearchContainer: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const { pushAlert, query } = useContext(PageContext);
  const [airlineIataFilter, setAirlineIataFilter] = useState(
    getQueryField(query, 'airlineIata', true),
  );
  const [flightNumberFilter, setFlightNumberFilter] = useState(
    getQueryField(query, 'flightNumber', true),
  );
  const [searchedAirlineIataFilter, setSearchedAirlineIataFilter] = useState(
    '',
  );
  const [searchedFlightNumberFilter, setSearchedFlightNumberFilter] = useState(
    '',
  );
  const [departureDateFilter, setDepartureDateFilter] = useState(
    query.departureDate ?? DateTime.now().toFormat(FLIGHT_SEARCH_DATE_FORMAT),
  );
  const [arrivalDateFilter, setArrivalDateFilter] = useState(
    query.arrivalDate ?? '',
  );
  const [departureAirportIataFilter, setDepartureAirportIataFilter] = useState(
    getQueryField(query, 'departureAirportIata', true),
  );
  const [arrivalAirportIataFilter, setArrivalAirportIataFilter] = useState(
    getQueryField(query, 'arrivalAirportIata', true),
  );
  const [statusFilter, setStatusFilter] = useState(
    getQueryField(query, 'status', true),
  );
  const [data, setData] = useState<ProcessedFlightHistory[]>([]);

  const OnSubmit = (isSubmitting: boolean = true) => {
    setLoading(true);

    // When a back/forward button is used, use the query params directly instead of
    // the input value since the async input update may not have the correct value in time
    flightSearch(
      isSubmitting
        ? airlineIataFilter
        : getQueryField(query, 'airlineIata', true),
      isSubmitting
        ? flightNumberFilter
        : getQueryField(query, 'flightNumber', true),
      isSubmitting
        ? departureDateFilter
        : getQueryField(query, 'departureDate', true),
      isSubmitting
        ? arrivalDateFilter
        : getQueryField(query, 'arrivalDate', true),
      isSubmitting
        ? departureAirportIataFilter
        : getQueryField(query, 'departureAirportIata', true),
      isSubmitting
        ? arrivalAirportIataFilter
        : getQueryField(query, 'arrivalAirportIata', true),
      isSubmitting
        ? statusFilter?.replace('ANY', '')
        : getQueryField(query, 'status', true)?.replace('ANY', ''),
    )
      .then(result => {
        setSearchedAirlineIataFilter(airlineIataFilter);
        setSearchedFlightNumberFilter(flightNumberFilter);
        setData(result);

        if (result.length === 100)
          pushAlert('info', 'Search results have been limited to 100 flights');
      })
      .catch(() => {
        setData([]);
        pushAlert('danger', 'Something went wrong searching for flights');
      })
      .finally(() => {
        // Only push a new search history if this is executed on submit and not back/forward
        if (isSubmitting) {
          const params: Record<string, string> = {};
          airlineIataFilter && (params.airlineIata = airlineIataFilter);
          flightNumberFilter && (params.flightNumber = flightNumberFilter);
          departureDateFilter &&
            (params.departureDate = departureDateFilter ?? '');
          arrivalDateFilter && (params.arrivalDate = arrivalDateFilter.toString() ?? '');
          departureAirportIataFilter &&
            (params.departureAirportIata = departureAirportIataFilter);
          arrivalAirportIataFilter &&
            (params.arrivalAirportIata = arrivalAirportIataFilter);
          statusFilter && (params.status = statusFilter);

          navigate({
            pathname: location.pathname,
            search: qs.stringify(params), // qs here to avoid state race conditions with setting/accessing urlsearchparams
          });
        }
        setLoading(false);
      });
  };

  // This forces the inputs values to correctly match the query params
  // when the back or forward buttons are used. It will not update the history
  // when it reexecutes the query for that page state
  useEffect(() => {
    // Simulate initial loading delay
    const initialLoadTimer = setTimeout(() => {
      setLoading(false);
    }, 1000); // Adjust the delay as needed
    setAirlineIataFilter(getQueryField(query, 'airlineIata', true));
    setFlightNumberFilter(getQueryField(query, 'flightNumber', true));
    setArrivalDateFilter(getQueryField(query, 'arrivalDate', true));
    setDepartureAirportIataFilter(
      getQueryField(query, 'departureAirportIata', true),
    );
    setArrivalAirportIataFilter(
      getQueryField(query, 'arrivalAirportIata', true),
    );
    setStatusFilter(getQueryField(query, 'status', true));
    // If there are params, reload the data with those
    if (location.search) {
      setDepartureDateFilter(getQueryField(query, 'departureDate', true));
    } else {
      // if there are no params, reset to the default page state
      setDepartureDateFilter(DateTime.now().toFormat(FLIGHT_SEARCH_DATE_FORMAT));
      setData([]);
    }
  }, [query]);

  const onClear = () => {
    setData([]);
  }

  return (
    <PageContentWrapper loading={loading} title="FLIGHT SEARCH">
      {loading && <FullPageLoadingSpinner />} {/* Conditionally render the spinner */}
      <BodyCard>
        <FlightSearchFilter
          airlineIataFilter={airlineIataFilter}
          setAirlineIataFilter={setAirlineIataFilter}
          flightNumberFilter={flightNumberFilter}
          setFlightNumberFilter={setFlightNumberFilter}
          departureDateFilter={departureDateFilter}
          setDepartureDateFilter={setDepartureDateFilter}
          arrivalDateFilter={arrivalDateFilter}
          setArrivalDateFilter={setArrivalDateFilter}
          departureAirportIataFilter={departureAirportIataFilter}
          setDepartureAirportIataFilter={setDepartureAirportIataFilter}
          arrivalAirportIataFilter={arrivalAirportIataFilter}
          setArrivalAirportIataFilter={setArrivalAirportIataFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          loading={loading}
          onSubmit={OnSubmit}
          onClear={onClear}
        />
      </BodyCard>
      <FlightSearchResultsTable
        data={data}
        filterCarrierIata={searchedAirlineIataFilter}
        filterFlightNum={searchedFlightNumberFilter}
      />
    </PageContentWrapper>
  );
};

export default FlightSearchContainer;
